import styles from './activity.module.css';
import { ActivityHero, ActivityPage, ActivityRewardCard } from './activity-cards';
import { GameplayView } from './gameplay-view';
import { Chip } from '../../../components/chip';
import { Card } from '../../../components/card';
import { Button } from '../../../components/button';
import { FormMessage } from '../../../components/form-field';

import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {

  useStudentCampaignArticle,
  useStudentMaterialArticle,

  useCompleteActivity,

} from '../../../api/student/campaigns/hooks';
import type {
  StudentCampaignDetail as Detail,
  StudentCampaignChapter,
  StudentActivity,
  StudentChapterMaterial,
} from '../../../api/student/campaigns/types';
import { chapterAvailability, noMoreSessions } from '../../../api/student/campaigns/gating';
import { shutWords } from './chapter-shut';

/**
 * Activity detail — the shell that picks a body by activity type, and the
 * Article / Video / Quiz bodies themselves. Gameplay lives in gameplay-view.
 */

interface MaterialDetailViewProps {
  campaignId: string;
  campaignName: string;
  chapter: StudentCampaignChapter;
  material: StudentChapterMaterial;
  onBackToCampaign: () => void;
  onBackToChapter: () => void;
}

export function MaterialDetailView({ campaignId, campaignName, chapter, material, onBackToCampaign, onBackToChapter }: MaterialDetailViewProps) {
  const { t } = useTranslation();
  const articleQuery = useStudentMaterialArticle(campaignId, material.id, material.type === 'ARTICLE');

  const videoUrl = (material.payload as { url?: string }).url;

  return (
    <div className="page">
      <nav className="crumb">
        <button type="button" onClick={onBackToCampaign}>{campaignName}</button>
        <span className="sep">›</span>
        <button type="button" onClick={onBackToChapter}>{chapter.title}</button>
        <span className="sep">›</span>
        <span>{material.title}</span>
      </nav>

      <article>
        <ActivityHero image={material.imageUrl} kind={material.type} title={material.title}>
          <Chip>{t('student.campaign.extraMaterials')}</Chip>
        </ActivityHero>
        {material.type === 'ARTICLE' ? (
          <Card framed padded={false} className={`${styles.studentArticle__body}`}>
            <ArticleContent query={articleQuery} />
          </Card>
        ) : (
          <Card framed padded={false} className={`${styles.studentVideo__frame}`}>
            {videoUrl
              ? <iframe src={videoUrl} title={material.title} allowFullScreen />
              : <p>{t('student.campaigns.videoMissing')}</p>
            }
          </Card>
        )}
      </article>
    </div>
  );
}

// ── Activity detail sub-view ───────────────────

interface ActivityDetailViewProps {
  campaign: Detail;
  chapter: StudentCampaignChapter;
  activity: StudentActivity;
  onBackToCampaign: () => void;
  onBackToChapter: () => void;
  onCompleted: () => Promise<void> | void;
}

export function ActivityDetailView({ campaign, chapter, activity, onBackToCampaign, onBackToChapter, onCompleted }: ActivityDetailViewProps) {
  const { t } = useTranslation();
  const campaignId = campaign.id;
  const campaignName = campaign.name;
  const isDone = !!activity.completion;
  // Whether its chapter is open, and if not, the first reason why: a campaign's end date does not close it.
  // The reason is said in its reward card, where the way to complete it would be.
  const { chapterOpen, shut: [why] } = chapterAvailability(chapter, campaign);
  const canComplete = chapterOpen && !isDone;
  const shutNote = why && !isDone ? shutWords(t, why, chapter) : null;

  return (
    <div className="page">
      <nav className="crumb">
        <button type="button" onClick={onBackToCampaign}>{campaignName}</button>
        <span className="sep">›</span>
        <button type="button" onClick={onBackToChapter}>{chapter.title}</button>
        <span className="sep">›</span>
        <span>{activity.title}</span>
      </nav>

      {activity.type === 'ARTICLE' && (
        <ArticleView
          campaignId={campaignId}
          activity={activity}
          canComplete={canComplete}
          isDone={isDone}
          shutNote={shutNote}
          onCompleted={onCompleted}
        />
      )}
      {activity.type === 'VIDEO' && (
        <VideoView campaignId={campaignId} activity={activity} canComplete={canComplete} isDone={isDone} shutNote={shutNote} onCompleted={onCompleted} />
      )}
      {activity.type === 'QUIZ' && (
        <QuizView campaignId={campaignId} activity={activity} canComplete={canComplete} isDone={isDone} shutNote={shutNote} onCompleted={onCompleted} />
      )}
      {activity.type === 'GAMEPLAY' && (
        <GameplayView
          campaignId={campaignId}
          activity={activity}
          noMoreSessions={noMoreSessions(activity, campaign)}
          kpBalance={campaign.kpBalance}
          shutNote={shutNote}
          onCompleted={onCompleted}
        />
      )}
    </div>
  );
}

interface ActivityBodyProps {
  campaignId: string;
  activity: StudentActivity;
  canComplete: boolean;
  isDone: boolean;
  /** Why it cannot be completed yet, while its chapter is shut. */
  shutNote: string | null;
  onCompleted: () => Promise<void> | void;
}

/** Completing the activity, then what its page does next: the call — with the result a quiz gives — and whether it is under way. Article, video and quiz complete alike. */
function useCompletion(campaignId: string, activityId: string, onCompleted: () => Promise<void> | void) {
  const mutation = useCompleteActivity();
  return {
    submitting: mutation.isPending,
    failed: mutation.isError,
    complete: (result?: Record<string, unknown>) =>
      mutation.mutate({ campaignId, activityId, result }, { onSuccess: () => onCompleted() }),
  };
}

/** A successful HTTP response still needs readable markdown before completion. */
function readableMarkdown(data: { markdown: string } | undefined): string | null {
  return typeof data?.markdown === 'string' && data.markdown.trim().length > 0 ? data.markdown : null;
}

/** Activity and reference articles share the same loading, failure and retry UI. */
function ArticleContent({ query }: { query: ReturnType<typeof useStudentCampaignArticle> }) {
  const { t } = useTranslation();
  const markdown = readableMarkdown(query.data);
  const ready = query.isSuccess && markdown !== null;
  return (
    <>
      {query.isPending && <p>{t('common.loading')}</p>}
      {!query.isPending && !ready && (
        <>
          <FormMessage>{t('student.campaigns.articleLoadFailed')}</FormMessage>
          <Button type="button" onClick={() => void query.refetch()} disabled={query.isFetching}>
            {t('student.campaigns.retry')}
          </Button>
        </>
      )}
      {ready && (
        <div className={`${styles.studentArticle__markdown}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
        </div>
      )}
    </>
  );
}

function ArticleView({ campaignId, activity, canComplete, isDone, shutNote, onCompleted }: ActivityBodyProps) {
  const { t } = useTranslation();
  const payload = activity.payload as { readingMinutes?: number; learningPoints?: string[] };
  const articleQuery = useStudentCampaignArticle(campaignId, activity.id);
  const ready = articleQuery.isSuccess && readableMarkdown(articleQuery.data) !== null;
  const { complete, submitting, failed } = useCompletion(campaignId, activity.id, onCompleted);

  return (
    <ActivityPage
      activity={activity}
      chips={payload.readingMinutes ? (
        <Chip>⏱ {t('student.campaign.readingMinutes', { minutes: payload.readingMinutes })}</Chip>
      ) : null}
      reward={
        <>
          <ActivityRewardCard
            activity={activity}
            done={isDone}
            shutNote={shutNote}
            action={canComplete ? { words: t('student.campaigns.markRead'), onClick: () => { if (ready && !submitting) complete(); }, busy: submitting || !ready } : null}
          />
          {failed && <FormMessage>{t('student.campaigns.completionFailed')}</FormMessage>}
        </>
      }
    >
      <Card framed className={`${styles.studentArticle__body}`}>
        <ArticleContent query={articleQuery} />
      </Card>
    </ActivityPage>
  );
}

function VideoView({ campaignId, activity, canComplete, isDone, shutNote, onCompleted }: ActivityBodyProps) {
  const { t } = useTranslation();
  const url = (activity.payload as { url?: string }).url ?? '';
  const { complete, submitting, failed } = useCompletion(campaignId, activity.id, onCompleted);

  return (
    <ActivityPage
      activity={activity}
      reward={
        <>
          <ActivityRewardCard
            activity={activity}
            done={isDone}
            shutNote={shutNote}
            action={canComplete ? { words: t('student.campaigns.markWatched'), onClick: () => complete(), busy: submitting } : null}
          />
          {failed && <FormMessage>{t('student.campaigns.completionFailed')}</FormMessage>}
        </>
      }
    >
      <Card framed className={`${styles.studentVideo__frame}`}>
        {url ? (
          <iframe src={url} title={activity.title} allowFullScreen />
        ) : (
          <p>{t('student.campaigns.videoMissing')}</p>
        )}
      </Card>
    </ActivityPage>
  );
}

interface QuizQuestion {
  id: string;
  prompt: string;
  choices: string[];
  correctIndex: number;
}

function QuizView({ campaignId, activity, canComplete, isDone, shutNote, onCompleted }: ActivityBodyProps) {
  const { t } = useTranslation();
  const questions = ((activity.payload as { questions?: QuizQuestion[] }).questions ?? []);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [score, setScore] = useState<number | null>(null);
  const { complete, submitting, failed } = useCompletion(campaignId, activity.id, onCompleted);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canComplete || submitting) return;
    let correct = 0;
    for (const q of questions) {
      if (answers[q.id] === q.correctIndex) correct++;
    }
    setScore(correct);
    complete({ answers, score: correct, total: questions.length });
  }

  return (
    <ActivityPage
      activity={activity}
      reward={
        <>
          <ActivityRewardCard
            activity={activity}
            done={isDone}
            shutNote={shutNote}
            action={canComplete ? { words: t('student.campaigns.submitQuiz'), form: 'student-quiz-form', busy: submitting } : null}
          />
          {failed && <FormMessage>{t('student.campaigns.completionFailed')}</FormMessage>}
        </>
      }
    >
      <Card as="form" framed className={`${styles.studentQuiz}`} id="student-quiz-form" onSubmit={handleSubmit}>
        {questions.map((q, qi) => (
          <fieldset key={q.id} className={`${styles.studentQuiz__question}`}>
            <legend>{qi + 1}. {q.prompt}</legend>
            {q.choices.map((c, ci) => (
              <label key={ci} className={`${styles.studentQuiz__choice}`}>
                <input
                  type="radio"
                  name={q.id}
                  checked={answers[q.id] === ci}
                  onChange={() => setAnswers((a) => ({ ...a, [q.id]: ci }))}
                  disabled={!canComplete}
                />
                {c}
              </label>
            ))}
          </fieldset>
        ))}
        {score !== null && (
          <p className={`${styles.studentQuiz__score}`}>
            {t('student.campaigns.quizScore', { score, total: questions.length })}
          </p>
        )}
      </Card>
    </ActivityPage>
  );
}

